import { IconButton } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { BarLoader } from "react-spinners";
import MessageStoreBase from "stores/RealTimeMessages/MessageStoreBase";
import hexToRGB from "utils/hexToRGB";
import { useStore } from "utils/useStore";
import { LayoutDrawerStore } from "../../stores/Layout/LayoutDrawerStore";
import theme from "../../Theme/AppTheme";
import NotificationDrawerContent from "./Views/NotificationDrawerContent";
import { sxClsx } from "utils/styleClsx";

interface NotificationMenuIconProps {
    activeButtonBackground?: string;
    inactiveButtonBackground?: string;
    activeIconColor?: string;
    inactiveIconColor?: string;
    loaderColor?: string;

    drawerAnchor?: "left" | "right" | "top" | "bottom";
    drawerVariant?: "permanent" | "persistent" | "temporary";
    drawerOffsetPx?: number;
    drawerOffsetPosition?: "Left" | "Top" | "Right" | "Bottom";
}

type Props = NotificationMenuIconProps;

const NotificationMenuIcon = observer((props: Props) => {
    const notificationStore = useStore(MessageStoreBase);
    const layoutDrawerStore = useStore(LayoutDrawerStore);

    useEffect(() => {
        layoutDrawerStore.setAnchor(props.drawerAnchor ?? "right");
        layoutDrawerStore.setCollapseSize("full");
        layoutDrawerStore.setVariant(props.drawerVariant ?? "permanent");
        layoutDrawerStore.setOffsetPixels(props.drawerOffsetPx ?? 1);
        layoutDrawerStore.setOffsetPosition(
            props.drawerOffsetPosition ?? "Top",
        );
    }, [
        props.drawerOffsetPosition,
        props.drawerOffsetPx,
        props.drawerVariant,
        props.drawerAnchor,
        layoutDrawerStore,
    ]);

    function handleUserClick(event: React.MouseEvent<HTMLButtonElement>) {
        if (layoutDrawerStore.isOpen) {
            layoutDrawerStore.closeDrawer();
            layoutDrawerStore.reset();
        } else {
            layoutDrawerStore.reset();
            layoutDrawerStore.setCustomRootStyles({
                zIndex: theme.zIndex.appBar - 1,
            });
            layoutDrawerStore.setAnchor(props.drawerAnchor ?? "right");
            layoutDrawerStore.setCollapseSize("full");
            layoutDrawerStore.setVariant(props.drawerVariant ?? "permanent");
            layoutDrawerStore.setOffsetPosition(
                props.drawerOffsetPosition ?? "Top",
            );

            layoutDrawerStore.setContentFactory(() => (
                <NotificationDrawerContent
                    onClose={layoutDrawerStore.closeDrawer}
                />
            ));
            layoutDrawerStore.setOpen(true);
        }
    }

    const isActive = notificationStore.notifications.length > 0;

    return (
        <div style={{ position: "relative" }}>
            <IconButton
                edge="end"
                aria-label="account of current user"
                aria-controls={"profile-menu"}
                aria-haspopup="true"
                onClick={handleUserClick}
                size="small"
                sx={sxClsx(
                    isActive &&
                        props.activeButtonBackground != null && {
                            backgroundColor: hexToRGB(
                                theme.palette.secondary.main,
                                0.1,
                            ),
                        },
                    !isActive &&
                        props.inactiveButtonBackground != null && {
                            backgroundColor: hexToRGB(
                                theme.palette.black.main,
                                0.05,
                            ),
                        },
                    {
                        "&:hover": {
                            backgroundColor: hexToRGB(
                                theme.palette.black.main,
                                0.05,
                            ),
                            color: hexToRGB(theme.palette.secondary.main, 0.25),
                        },
                    },
                )}
            >
                {isActive ? (
                    <NotificationsActiveIcon
                        sx={{
                            color:
                                props.activeIconColor ??
                                theme.palette.gray[600],
                        }}
                    />
                ) : (
                    <NotificationsIcon
                        sx={{
                            color:
                                props.inactiveIconColor ??
                                theme.palette.gray[400],
                        }}
                    />
                )}
            </IconButton>
            {notificationStore.anyRunningOperations && (
                <div style={{ position: "absolute" }}>
                    <BarLoader
                        color={
                            props.loaderColor ?? theme.palette.secondary.light
                        }
                        height={3}
                        width={28}
                        loading={true}
                    />
                </div>
            )}
        </div>
    );
});

export default NotificationMenuIcon;
